import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckSquare, Check, X, ShieldCheck, Play, Clock, Send, CalendarClock, AlertTriangle, Ban, Copy } from "lucide-react";
import { PageBody, PageHeader, EmptyState } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { DateTimePicker } from "@/components/DateTimePicker";
import { ManualSendBanner } from "@/components/ManualSendBanner";
import api from "@/lib/api";

function LoomPreview({ url }) {
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    let ok = true;
    api.get("/loom/oembed", { params: { url } }).then(({ data }) => { if (ok) setThumb(data?.thumbnail_url || null); }).catch(() => {});
    return () => { ok = false; };
  }, [url]);
  return (
    <div className="mt-3 rounded-lg border border-border/50 bg-accent/30 p-3" data-testid="approval-loom">
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Loom walkthrough</div>
      {thumb ? (
        <a href={url} target="_blank" rel="noreferrer" data-testid="approval-loom-link">
          <img src={thumb} alt="Loom walkthrough" className="w-48 rounded-md border border-border/60 hover:opacity-90 transition-opacity" data-testid="approval-loom-thumb" />
        </a>
      ) : (
        <a href={url} target="_blank" rel="noreferrer" className="text-sm text-sky-500 hover:underline break-all inline-flex items-center gap-1.5" data-testid="approval-loom-link">
          <Play className="h-3.5 w-3.5 shrink-0" /> {url}
        </a>
      )}
    </div>
  );
}

const STATUS_META = {
  scheduled: { label: "Approved — scheduled", cls: "text-emerald-500", Icon: Clock },
  blocked: { label: "Blocked", cls: "text-amber-500", Icon: Ban },
  failed: { label: "Send failed", cls: "text-rose-500", Icon: AlertTriangle },
  reminder: { label: "Reminder", cls: "text-sky-500", Icon: CalendarClock },
  reminder_due: { label: "Reminder", cls: "text-sky-500", Icon: CalendarClock },
};

export default function Approvals() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState({});
  const [sched, setSched] = useState({});     // per-item scheduling choice: { mode: 'now'|'later', at }
  const [scheduled, setScheduled] = useState([]);
  const [resc, setResc] = useState({});
  const [tz, setTz] = useState("UTC");
  const [manual, setManual] = useState(false);
  const [rwOpen, setRwOpen] = useState(null);
  const [rwNote, setRwNote] = useState("");

  const load = async () => {
    const [{ data }, { data: s }, { data: st }] = await Promise.all([
      api.get("/approvals"), api.get("/scheduled-sends"), api.get("/org/status"),
    ]);
    setItems(data); setScheduled(s); setTz(st.timezone || "UTC"); setManual(!!st.manual_send);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const copyText = (t, label) => { navigator.clipboard.writeText(t || ""); toast.success(`${label} copied`); };
  const markSent = async (e) => {
    const patch = edit[e.id] || {};
    await api.post(`/approvals/${e.id}/mark-sent`, { draft_subject: patch.subject ?? e.draft_subject, draft_body: patch.body ?? e.draft_body });
    toast.success(`Marked sent to ${e.contact_name}`); load();
  };
  const scheduleReminder = async (e) => {
    const at = (sched[e.id] || {}).at;
    if (!at) return toast.error("Pick a reminder date and time");
    const { data } = await api.post(`/approvals/${e.id}/reminder`, { remind_at: at });
    toast.success(`Reminder set for ${data.send_at_display}`); load();
  };

  const approve = async (e) => {
    const patch = edit[e.id] || {};
    const choice = sched[e.id] || { mode: "now", at: "" };
    if (choice.mode === "later" && !choice.at) return toast.error("Pick a send date and time, or choose Send now");
    try {
      const { data } = await api.post(`/approvals/${e.id}/approve`, {
        draft_subject: patch.subject ?? e.draft_subject,
        draft_body: patch.body ?? e.draft_body,
        send_at: choice.mode === "later" ? choice.at : null,
      });
      if (data.status === "scheduled") toast.success(`Approved — scheduled for ${data.send_at_display}`);
      else if (data.status === "sent") toast.success(`Approved — sent to ${e.contact_name}`);
      else if (data.status === "blocked") toast.warning(`Approved — blocked. ${data.block_reason || ""}`);
      else if (data.status === "failed") toast.error(`Send failed. ${data.fail_reason || ""}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail?.message || "Could not approve"); }
  };
  const reject = async (e) => { await api.post(`/approvals/${e.id}/reject`); toast("Rejected", { description: e.contact_name }); load(); };
  const rewriteApproval = async (e, note) => {
    try { const { data } = await api.post(`/approvals/${e.id}/rewrite`, { note: note || "" }); setEdit((p) => ({ ...p, [e.id]: { subject: data.draft_subject, body: data.draft_body } })); toast.success("Step rewritten"); setRwOpen(null); setRwNote(""); }
    catch (err) { toast.error(err?.response?.data?.detail?.message || "Rewrite failed"); }
  };

  const reschedule = async (e) => {
    const v = resc[e.id];
    if (!v) return toast.error("Pick a new date/time");
    try { const { data } = await api.put(`/scheduled-sends/${e.id}`, { send_at: v }); toast.success(`Rescheduled for ${data.send_at_display}`); setResc({ ...resc, [e.id]: "" }); load(); }
    catch { toast.error("Could not reschedule"); }
  };
  const cancelSend = async (e) => { await api.post(`/scheduled-sends/${e.id}/cancel`); toast("Canceled", { description: e.contact_name }); load(); };

  const campIds = [...new Set(items.map((i) => i.campaign_id).filter(Boolean))];
  const oneCampaign = items.length > 0 && campIds.length === 1;
  const approveAllSamples = async () => {
    if (!oneCampaign) return;
    try {
      const { data } = await api.post("/approvals/approve-all-samples", { campaign_id: campIds[0] });
      const parts = [];
      if (data.sent) parts.push(`${data.sent} sent`);
      if (data.blocked) parts.push(`${data.blocked} blocked`);
      if (data.failed) parts.push(`${data.failed} failed`);
      if (data.skipped) parts.push(`${data.skipped} skipped (no source URL)`);
      toast.success(`Samples approved${parts.length ? " · " + parts.join(" · ") : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not approve samples"); }
  };

  return (
    <PageBody>
      <PageHeader eyebrow="First-Touch Approval Queue" title="Nothing off-brand ships"
        subtitle="Senior gatekeeper mode. Inspect and edit every first-touch email, then send now or schedule it. Scheduling never skips this approval." />
      <ManualSendBanner />
      {items.length === 0 ? (
        <EmptyState icon={CheckSquare} title="Queue is clear" body="Enrolled first-touch drafts will appear here for review." />
      ) : (
        <div className="space-y-4">
          {oneCampaign && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="approve-all-samples-bar">
              <div className="text-sm pr-3"><span className="font-medium">{items.length} first-touch sample{items.length === 1 ? "" : "s"}</span> <span className="text-muted-foreground">for this campaign. Approving keeps the first-touch policy — the rest auto-send per your enroll rules.</span></div>
              <Button size="sm" className="rounded-md bg-amber-500 text-black hover:bg-amber-400 shrink-0" onClick={approveAllSamples} data-testid="approve-all-samples-btn"><Check className="h-3.5 w-3.5 mr-1.5" /> Approve all samples</Button>
            </div>
          )}
          {items.map((e) => {
            const ed = edit[e.id] || {};
            const choice = sched[e.id] || { mode: "now", at: "" };
            return (
              <div key={e.id} className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid={`approval-item-${e.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <span className="h-9 w-9 rounded-full bg-amber-500/15 text-amber-500 flex items-center justify-center text-sm font-semibold">{e.contact_name?.slice(0, 1)}</span>
                    <div>
                      <div className="font-medium text-sm">{e.contact_name} <span className="text-muted-foreground font-normal">· {e.contact_title}</span></div>
                      <div className="text-xs text-muted-foreground">{e.contact_company} · <span className="font-mono">{e.contact_email}</span></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-500" /><Badge variant="outline" className="text-xs">first-touch</Badge>
                  </div>
                </div>
                <Input value={ed.subject ?? e.draft_subject ?? ""} onChange={(ev) => setEdit({ ...edit, [e.id]: { ...ed, subject: ev.target.value } })} className="mb-2 font-medium" data-testid={`approval-subject-${e.id}`} />
                <Textarea value={ed.body ?? e.draft_body ?? ""} onChange={(ev) => setEdit({ ...edit, [e.id]: { ...ed, body: ev.target.value } })} rows={6} className="text-sm" data-testid={`approval-body-${e.id}`} />
                {e.signature && (
                  <div className="mt-2 rounded-md border border-border/40 bg-accent/20 p-2.5" data-testid={`approval-signature-${e.id}`}>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Signature · appended on send</div>
                    <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-sans m-0">{e.signature}</pre>
                  </div>
                )}
                {e.loom_url && <LoomPreview url={e.loom_url} />}

                {manual ? (
                  <>
                    <div className="mt-4 rounded-lg border border-sky-500/30 bg-sky-500/5 p-3" data-testid={`manual-actions-${e.id}`}>
                      <p className="text-xs text-muted-foreground mb-3">Connect work email to send from DealsOps. Until then, copy and send from your inbox.</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" className="rounded-md" onClick={() => copyText(ed.subject ?? e.draft_subject, "Subject")} data-testid={`copy-subject-${e.id}`}><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy subject</Button>
                        <Button size="sm" variant="outline" className="rounded-md" onClick={() => copyText(ed.body ?? e.draft_body, "Body")} data-testid={`copy-body-${e.id}`}><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy body</Button>
                        <Button size="sm" variant="outline" className="rounded-md" onClick={() => { setRwOpen(rwOpen === e.id ? null : e.id); setRwNote(""); }} data-testid={`rewrite-step-${e.id}`}>Rewrite this step</Button>
                        <Button size="sm" className="rounded-md bg-emerald-500 text-black hover:bg-emerald-400" onClick={() => markSent(e)} data-testid={`mark-sent-${e.id}`}><Check className="h-3.5 w-3.5 mr-1.5" /> Mark sent</Button>
                        <Button size="sm" variant={choice.mode === "reminder" ? "default" : "outline"} className="rounded-md" onClick={() => setSched({ ...sched, [e.id]: { mode: "reminder", at: choice.at } })} data-testid={`reminder-${e.id}`}><CalendarClock className="h-3.5 w-3.5 mr-1.5" /> Schedule reminder</Button>
                        {choice.mode === "reminder" && <DateTimePicker value={choice.at} tz={tz} onChange={(v) => setSched({ ...sched, [e.id]: { mode: "reminder", at: v } })} testId={`reminder-picker-${e.id}`} className="h-9" placeholder="Remind me at" />}
                        {choice.mode === "reminder" && <Button size="sm" variant="outline" className="rounded-md" onClick={() => scheduleReminder(e)} data-testid={`set-reminder-${e.id}`}>Set reminder</Button>}
                      </div>
                      {rwOpen === e.id && (
                        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`rewrite-note-row-${e.id}`}>
                          <Input value={rwNote} onChange={(ev) => setRwNote(ev.target.value)} placeholder="shorter / more urgent / no jargon" className="text-sm h-9 flex-1" data-testid={`rewrite-note-input-${e.id}`} onKeyDown={(ev) => { if (ev.key === "Enter") rewriteApproval(e, rwNote); }} />
                          <div className="flex items-center gap-2">
                            <Button size="sm" onClick={() => rewriteApproval(e, rwNote)} className="rounded-md bg-amber-500 text-black hover:bg-amber-400" data-testid={`rewrite-note-submit-${e.id}`}>Rewrite</Button>
                            <Button size="sm" variant="ghost" onClick={() => { setRwOpen(null); setRwNote(""); }} data-testid={`rewrite-note-cancel-${e.id}`}>Cancel</Button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button variant="ghost" onClick={() => reject(e)} data-testid={`reject-btn-${e.id}`} className="text-rose-500 hover:text-rose-500"><X className="h-4 w-4 mr-1.5" /> Reject</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mt-4 rounded-lg border border-border/50 bg-accent/20 p-3">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">When to send</div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" variant={choice.mode === "now" ? "default" : "outline"} className="rounded-md" onClick={() => setSched({ ...sched, [e.id]: { mode: "now", at: "" } })} data-testid={`send-now-${e.id}`}>
                          <Send className="h-3.5 w-3.5 mr-1.5" /> Send now
                        </Button>
                        <Button size="sm" variant={choice.mode === "later" ? "default" : "outline"} className="rounded-md" onClick={() => setSched({ ...sched, [e.id]: { mode: "later", at: choice.at } })} data-testid={`schedule-${e.id}`}>
                          <CalendarClock className="h-3.5 w-3.5 mr-1.5" /> Schedule
                        </Button>
                        {choice.mode === "later" && (
                          <DateTimePicker value={choice.at} tz={tz} onChange={(v) => setSched({ ...sched, [e.id]: { mode: "later", at: v } })} testId={`approval-picker-${e.id}`} className="h-9" />
                        )}
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2 justify-end">
                      <Button variant="ghost" onClick={() => reject(e)} data-testid={`reject-btn-${e.id}`} className="text-rose-500 hover:text-rose-500"><X className="h-4 w-4 mr-1.5" /> Reject</Button>
                      <Button onClick={() => approve(e)} data-testid="approve-touch-btn" className="rounded-md bg-emerald-500 text-black hover:bg-emerald-400">
                        <Check className="h-4 w-4 mr-1.5" /> {choice.mode === "later" ? "Approve & schedule" : "Approve & send"}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {scheduled.length > 0 && (
        <div className="mt-10" data-testid="scheduled-sends">
          <div className="eyebrow mb-3 flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> Approved · scheduled &amp; blocked</div>
          <div className="space-y-3">
            {scheduled.map((e) => {
              const meta = STATUS_META[e.status] || STATUS_META.scheduled;
              const StatusIcon = meta.Icon;
              return (
                <div key={e.id} className="rounded-xl border border-border/60 bg-card/40 p-4" data-testid={`scheduled-item-${e.id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{e.contact_name} <span className="text-muted-foreground font-normal">· {e.contact_company}</span></div>
                      <div className={`text-xs mt-0.5 flex items-center gap-1.5 ${meta.cls}`} data-testid={`scheduled-time-${e.id}`}>
                        <StatusIcon className="h-3.5 w-3.5 shrink-0" />
                        {e.status === "scheduled" ? `Approved — scheduled for ${e.send_at_display}`
                          : (e.status === "reminder" || e.status === "reminder_due") ? `Reminder — ${e.status === "reminder_due" ? "due now" : `due ${e.send_at_display}`}`
                          : `${meta.label}${e.block_reason || e.fail_reason ? ` — ${e.block_reason || e.fail_reason}` : ""}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      <DateTimePicker value={resc[e.id] || ""} tz={tz} onChange={(v) => setResc({ ...resc, [e.id]: v })} testId={`reschedule-input-${e.id}`} className="h-9" placeholder="New time" />
                      <Button size="sm" variant="outline" className="rounded-md" onClick={() => reschedule(e)} data-testid={`reschedule-btn-${e.id}`}>{e.status === "scheduled" ? "Reschedule" : "Retry at"}</Button>
                      <Button size="sm" variant="ghost" className="rounded-md text-rose-500" onClick={() => cancelSend(e)} data-testid={`cancel-send-${e.id}`}><X className="h-4 w-4" /></Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </PageBody>
  );
}
